import {
  Box,
  CircularProgress,
  Paper,
  Stack,
  SxProps,
  Typography,
} from "@mui/material";
import QuestionItem from "./QuestionItem";
import { useAppSelector } from "../../Redux/hooks";
import { useParams } from "react-router-dom";
import SurveyActions from "./Survey/SurveyActions";
import VisitSubmitButton from "./Visits/VisitSubmitButton";
import React, { useMemo, useRef } from "react";
import LockStatus from "./LockStatus";
import { useTranslation } from "react-i18next";
import { useScrollToTopOnFormChange } from "../../utils/useScrollToTopOnFormChange";
import { groupQuestionsForDisplay } from "../../utils/groupQuestionOrder";
import QuestionGroupSection from "../QuestionBuilder/QuestionGroupSection";

const responseContainer: SxProps = {
  flex: "1",
  minWidth: "1px",
};

const responseWrapper: SxProps = {
  width: "100%",
  px: 1,
  pb: 2,
  display: "flex",
  flexDirection: "column",
};

const responseWrapperHeader: SxProps = {
  p: 2,
  borderBottom: "1px solid",
  borderColor: "divider",
  mb: 2,
};

const QuestionItemWrapper = () => {
  const { t } = useTranslation("responses");
  const { surveySlug } = useParams();
  const formHeaderRef = useRef<HTMLDivElement>(null);
  const selectedForm = useAppSelector((state) => state.response.selectedForm);
  const isFormLoading = useAppSelector((state) => state.response.isLoading);
  const questionList = useAppSelector((state) => state.response.questionList);
  const isParticipant = useAppSelector((state) => state.response.isParticipant);
  const currentCategory = useAppSelector(
    (state) => state.response.currentCategory,
  );
  const canLock = useAppSelector((state) => state.response.canLock);

  useScrollToTopOnFormChange(formHeaderRef, selectedForm?.id, isFormLoading);

  const visibleQuestions = useMemo(
    () =>
      (questionList ?? []).filter(
        (question) =>
          question.isVisible &&
          (!question.properties.isHidden ||
            (["calculated_field", "statement"].includes(question.type) &&
              !isParticipant)),
      ),
    [questionList, isParticipant],
  );

  const displayItems = useMemo(
    () => groupQuestionsForDisplay(visibleQuestions),
    [visibleQuestions],
  );

  const indexById = useMemo(() => {
    const map = new Map<string, number>();
    visibleQuestions.forEach((question, index) => {
      map.set(question.id, index);
    });
    return map;
  }, [visibleQuestions]);

  return (
    <>
      {isFormLoading ? (
        <Stack
          sx={{ flex: 1, height: "100%" }}
          justifyContent={"center"}
          alignItems={"center"}
        >
          <CircularProgress />
        </Stack>
      ) : (
        <>
          {questionList?.length > 0 && (
            <Box sx={responseContainer}>
              <Paper sx={responseWrapper}>
                <Stack ref={formHeaderRef} sx={responseWrapperHeader}>
                  <Typography variant="h3">{selectedForm?.name}</Typography>
                  {/* <Typography variant="subtitle1">
                    {selectedForm?.phaseName}
                  </Typography> */}
                  <LockStatus selectedForm={selectedForm} />
                </Stack>

                {displayItems.map((item) => {
                  const question = item.question;
                  if (question.type === "group") {
                    if (item.members.length === 0) {
                      return null;
                    }
                    return (
                      <QuestionGroupSection
                        key={question.id}
                        question={question}
                        memberCount={item.members.length}
                        variant="response"
                      >
                        {item.members.map((member) => (
                          <QuestionItem
                            key={member.id}
                            index={indexById.get(member.id) ?? 0}
                            question={member}
                            isGroupMember
                          />
                        ))}
                      </QuestionGroupSection>
                    );
                  }

                  return (
                    <QuestionItem
                      key={question.id}
                      index={indexById.get(question.id) ?? 0}
                      question={question}
                    />
                  );
                })}
              </Paper>

              {surveySlug && <SurveyActions />}
              {currentCategory === "visit" &&
                !selectedForm?.locked &&
                !selectedForm?.isSigned &&
                canLock && (
                  <Box
                    sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}
                  >
                    <VisitSubmitButton />
                  </Box>
                )}
            </Box>
          )}

          {questionList?.length === 0 && (
            <Stack
              sx={{ flex: 1, height: "100%" }}
              justifyContent={"center"}
              alignItems={"center"}
            >
              <Typography sx={{ fontWeight: "medium" }} color={"primary.main"}>
                {t("SURVEY.NO_FORMS_FOUND")}
              </Typography>
            </Stack>
          )}
        </>
      )}
    </>
  );
};

export default React.memo(QuestionItemWrapper);
